import { z } from 'zod';
import { dateKeySchema } from '../money';
import { objectIdSchema, optionalObjectId, optionalText, paginationQuerySchema, passwordSchema, requiredEmail } from './common';

export const CLIENT_REQUEST_TYPES = ['modification', 'new_project', 'support', 'meeting', 'feedback'] as const;
export type ClientRequestType = (typeof CLIENT_REQUEST_TYPES)[number];
export const CLIENT_REQUEST_LABELS: Record<ClientRequestType, string> = {
  modification: 'Modification request',
  new_project: 'New project request',
  support: 'Support request',
  meeting: 'Meeting request',
  feedback: 'Feedback',
};
export const CLIENT_REQUEST_STATUSES = ['open', 'in_progress', 'resolved', 'closed'] as const;
export type ClientRequestStatus = (typeof CLIENT_REQUEST_STATUSES)[number];

/** Staff invite a client contact to the portal. */
export const portalUserCreateSchema = z.object({
  name: z.string().trim().min(2).max(120),
  email: requiredEmail(),
  temporaryPassword: passwordSchema,
});
export const portalUserStatusSchema = z.object({ status: z.enum(['active', 'suspended']) });

export const portalMessageSchema = z.object({ body: z.string().trim().min(1).max(4000) });

export const clientRequestCreateSchema = z
  .object({
    type: z.enum(CLIENT_REQUEST_TYPES),
    projectId: optionalObjectId(),
    subject: z.string().trim().min(3).max(160),
    details: z.string().trim().min(3).max(8000),
    /** Meeting requests: preferred date. */
    preferredDate: dateKeySchema.optional(),
    /** Feedback: 1–5. */
    rating: z.coerce.number().int().min(1).max(5).optional(),
  })
  .refine((v) => v.type !== 'feedback' || v.rating !== undefined, { message: 'Give a rating', path: ['rating'] })
  .refine((v) => !['modification', 'support', 'feedback'].includes(v.type) || !!v.projectId, { message: 'Choose the project', path: ['projectId'] });
export type ClientRequestCreateInput = z.input<typeof clientRequestCreateSchema>;

export const clientRequestReplySchema = z.object({ body: z.string().trim().min(1).max(4000) });
export const clientRequestUpdateSchema = z.object({
  status: z.enum(CLIENT_REQUEST_STATUSES).optional(),
  assignedTo: objectIdSchema.nullable().optional(),
  note: optionalText(500),
});
export const clientRequestListQuerySchema = paginationQuerySchema.extend({
  status: z.enum([...CLIENT_REQUEST_STATUSES, 'active'] as const).optional(),
  type: z.enum(CLIENT_REQUEST_TYPES).optional(),
  clientId: objectIdSchema.optional(),
  projectId: objectIdSchema.optional(),
});
